/**
 * Basic word utility functions for WordDrift
 */

import winkLemmatizer from 'wink-lemmatizer';
import type { GameWord, WordStringVariations } from './GameTypes';
import type { DictionaryEntry } from './DictionaryTypes';

// Type for wink-lemmatizer functions
type Lemmatizer = {
  noun: (word: string) => string | null;
  verb: (word: string) => string | null;
  adjective: (word: string) => string | null;
  adverb?: (word: string) => string | null;
};

const lemmatizer: Lemmatizer = winkLemmatizer as Lemmatizer;

/**
 * Create a GameWord from a display word
 * Sets onClickDestination based on dictionary presence
 */
export function createGameWord(displayWord: string, dictionary?: Record<string, DictionaryEntry> | null): GameWord {
  const displayLower = displayWord.toLowerCase();
  const lemmatized = enhancedLemmatizeWord(displayWord, dictionary);
  
  // Determine navigation destination:
  // If display form exists in dictionary, use it; otherwise use lemmatized form
  const onClickDestination = dictionary?.[displayLower] 
    ? displayLower 
    : lemmatized;
  
  return {
    variations: {
      display: displayWord,
      lemmatized,
      onClickDestination
    }
    // gameplay is undefined - populated during actual gameplay
  };
}

/**
 * Convert a string array to GameWord array
 */
export function createGameWordArray(words: string[], dictionary?: Record<string, DictionaryEntry> | null): GameWord[] {
  return words.map(word => createGameWord(word, dictionary));
}



/**
 * Enhanced lemmatization - uses dictionary for better accuracy
 */
export function enhancedLemmatizeWord(word: string, dictionary?: Record<string, DictionaryEntry> | null): string {
  if (!dictionary) {
    // Fallback to simple lemmatization if dictionary not provided
    const wordLower = word.toLowerCase();
    
    // Try wink-lemmatizer
    let nounLemma = lemmatizer.noun(wordLower);
    let verbLemma = lemmatizer.verb(wordLower);
    let adjectiveLemma = lemmatizer.adjective(wordLower);
    let adverbLemma = lemmatizer.adverb ? lemmatizer.adverb(wordLower) : null;
    
    if (nounLemma && nounLemma !== wordLower) return nounLemma;
    if (verbLemma && verbLemma !== wordLower) return verbLemma;
    if (adjectiveLemma && adjectiveLemma !== wordLower) return adjectiveLemma;
    if (adverbLemma && adverbLemma !== wordLower) return adverbLemma;
    
    return wordLower;
  }

  // Check if word exists as-is in dictionary
  if (dictionary[word.toLowerCase()]) {
    return word.toLowerCase();
  }
  
  // Try wink-lemmatizer
  let nounLemma = lemmatizer.noun(word.toLowerCase());
  let verbLemma = lemmatizer.verb(word.toLowerCase());
  let adjectiveLemma = lemmatizer.adjective(word.toLowerCase());
  let adverbLemma = lemmatizer.adverb ? lemmatizer.adverb(word.toLowerCase()) : null;
  
  if (nounLemma && nounLemma !== word.toLowerCase() && dictionary[nounLemma]) {
    return nounLemma;
  }
  if (verbLemma && verbLemma !== word.toLowerCase() && dictionary[verbLemma]) {
    return verbLemma;
  }
  if (adjectiveLemma && adjectiveLemma !== word.toLowerCase() && dictionary[adjectiveLemma]) {
    return adjectiveLemma;
  }
  if (adverbLemma && adverbLemma !== word.toLowerCase() && dictionary[adverbLemma]) {
    return adverbLemma;
  }

  // Manual plural handling
  let possibleBase = word.toLowerCase();
  
  if (possibleBase.endsWith('ies') && possibleBase.length > 4) {
    possibleBase = possibleBase.slice(0, -3) + 'y';
  } else if (possibleBase.endsWith('es') && possibleBase.length > 3) {
    possibleBase = possibleBase.slice(0, -2);
    if (!dictionary[possibleBase]) {
      possibleBase = possibleBase.slice(0, -1);
    }
  } else if (possibleBase.endsWith('s') && possibleBase.length > 2) {
    possibleBase = possibleBase.slice(0, -1);
  }
  
  if (possibleBase !== word.toLowerCase() && dictionary[possibleBase]) {
    return possibleBase;
  }
  
  return word.toLowerCase();
}

/**
 * Get all lemmatized forms of a word from the dictionary
 */
export function getLemmatizedForms(word: string, dictionary: Record<string, DictionaryEntry>): string[] {
  const entry = dictionary[word.toLowerCase()];
  if (!entry || !entry.lemmatizedForms) {
    return [word.toLowerCase()];
  }
  
  const allForms = new Set([word.toLowerCase(), ...entry.lemmatizedForms]);
  return Array.from(allForms);
}

/**
 * Check if any lemmatized form of word1 matches any lemmatized form of word2
 */
export function areWordsLemmaticallyRelated(word1: string, word2: string, dictionary: Record<string, DictionaryEntry>): boolean {
  const forms1 = getLemmatizedForms(word1, dictionary);
  const forms2 = getLemmatizedForms(word2, dictionary);
  
  return forms1.some(form1 => forms2.includes(form1));
}

/**
 * Check if a word is valid (exists in dictionary)
 */
export function isValidWord(word: string, dictionary: Record<string, DictionaryEntry>): boolean {
  return dictionary[word.toLowerCase()] !== undefined;
}

/**
 * Clean up a definition by ensuring proper capitalization and punctuation
 */
export function cleanDefinition(def: string): string {
  let cleaned = def.trim();
  
  // Ensure it starts with a capital letter
  if (cleaned.length > 0) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }
  
  // Ensure it ends with a period
  if (cleaned.length > 0 && !cleaned.endsWith('.') && !cleaned.endsWith('!') && !cleaned.endsWith('?')) {
    cleaned = cleaned + '.';
  }
  
  return cleaned;
}
